import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiChevronDown } from 'react-icons/fi';
import { BiBookAlt, BiMedal, BiAward } from 'react-icons/bi';
import { settingsAPI } from '../../services/api';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const fallbackPrograms = [
  { _id: '1', title: 'Montessori', grades: 'Age: ~2.5 – 5 years',
    description: 'Focuses on basic learning, play, and behavior to build a strong early foundation.',
    subjects: ['Play Group (PG)', 'Nursery', 'Lower Kindergarten (LKG)', 'Upper Kindergarten (UKG)'] },
  { _id: '2', title: 'Primary', grades: 'Age: ~6 – 10 years',
    description: 'Dedicated to foundational knowledge and mastery of basic subjects like Math, English, and Science.',
    subjects: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'] },
  { _id: '3', title: 'Lower Secondary', grades: 'Age: ~11 – 13 years',
    description: 'Designed for concept building, critical thinking, and introduction to more advanced subjects.',
    subjects: ['Class 6', 'Class 7', 'Class 8'] },
  { _id: '4', title: 'Secondary', grades: 'Age: ~14 – 16 years',
    description: 'Rigorous preparation for the SEE (School Leaving Certificate) under the National Examination Board.',
    subjects: ['Class 9', 'Class 10'] },
];

const icons = [BiBookAlt, BiMedal, BiAward, BiBookAlt];

const cardColors = [
  { strip: 'bg-[#2563EB]', icon: 'bg-[#2563EB]', badge: 'bg-[#2563EB]', text: 'text-[#2563EB]' },
  { strip: 'bg-[#1D4ED8]', icon: 'bg-[#1D4ED8]', badge: 'bg-[#1D4ED8]', text: 'text-[#1D4ED8]' },
  { strip: 'bg-[#0EA5E9]', icon: 'bg-[#0EA5E9]', badge: 'bg-[#0EA5E9]', text: 'text-[#0EA5E9]' },
  { strip: 'bg-[#1e40af]', icon: 'bg-[#1e40af]', badge: 'bg-[#1e40af]', text: 'text-[#1e40af]' },
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const AcademicsSection = () => {
  const [programs, setPrograms] = useState(fallbackPrograms);
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const data = res.data?.data?.settings?.programs || [];
      if (data.length > 0) setPrograms(data);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-14 lg:py-20 bg-[#F0F4FF] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 relative z-10">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={isVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="mb-10">
          <SectionLabel text="Academic Excellence" />
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] leading-tight mb-3">
            Our Educational <span className="text-[#2563EB]">Programs</span>
          </h2>
          <p className="text-slate-500 max-w-2xl text-base">
            Comprehensive academic programs designed to cater to every stage of a child's learning journey,
            from foundational years to career preparation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {programs.slice(0, 3).map((program, idx) => {
            const Icon = icons[idx % icons.length];
            const color = cardColors[idx % cardColors.length];
            return (
              <motion.div key={program._id || idx}
                initial={{ opacity: 0, y: 30 }} animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-blue-100 overflow-hidden flex flex-col hover:-translate-y-1.5 transition-all duration-300 group">
                <div className={`h-1 w-full ${color.strip}`} />
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 ${color.icon} rounded-xl flex items-center justify-center shadow-sm shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#0F172A] leading-tight">{program.title}</h3>
                      {program.grades && (
                        <span className={`inline-block px-2 py-0.5 ${color.badge} text-white text-[9px] font-bold uppercase tracking-widest rounded-full mt-1`}>
                          {program.grades}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4 flex-1">{program.description}</p>
                  {program.subjects?.length > 0 && (
                    <div className="mb-4 pt-3 border-t border-blue-50">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Levels / Classes</p>
                      <div className="flex flex-wrap gap-1.5">
                        {program.subjects.slice(0, 3).map((s, sIdx) => (
                          <span key={sIdx} className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-700 text-[10px] font-semibold rounded-md">{s}</span>
                        ))}
                        {program.subjects.length > 3 && (
                          <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-400 text-[10px] font-semibold rounded-md">+{program.subjects.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}
                  <Link to="/academics" className={`inline-flex items-center gap-1.5 text-sm font-bold ${color.text} group-hover:gap-2.5 transition-all mt-auto`}>
                    Learn More <FiArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* More Programs button — redirects to /academics */}
        <motion.div initial={{ opacity: 0 }} animate={isVisible ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.5 }}
          className="flex justify-center mt-8">
          <Link to="/academics"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-bold uppercase tracking-wider rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <FiChevronDown className="w-4 h-4" /> More Programs
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AcademicsSection;
